import React, { useEffect, useState, useMemo } from "react";
import { Search, Grid3x3, List as ListIcon, X, Activity, Wrench, Info as InfoIcon, MapPin } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from "recharts";
import { api } from "@/lib/api";
import { formatCount, formatPct, formatHMR, formatTemp } from "@/utils/formatters";
import PageHeader from "@/components/layout/PageHeader";
import GlassCard from "@/components/ui-fleet/GlassCard";
import ClickableKPITile from "@/components/ui-fleet/ClickableKPITile";
import StatusBadge from "@/components/ui-fleet/StatusBadge";
import FuelBar from "@/components/ui-fleet/FuelBar";
import MachineCard from "@/components/ui-fleet/MachineCard";

const CATEGORIES = ["all", "BHL", "EXC", "TEL", "LDR", "MINI"];
const STATUSES = ["all", "active", "idle", "alert", "maintenance"];
const SITES = [
  { id: "all", name: "All Sites" },
  { id: "S-MUM", name: "Mumbai" },
  { id: "S-PUN", name: "Pune" },
  { id: "S-DEL", name: "Delhi NCR" },
  { id: "S-BLR", name: "Bengaluru" },
  { id: "S-HYD", name: "Hyderabad" },
  { id: "S-CHN", name: "Chennai" },
];

export default function FleetMonitor() {
  const [machines, setMachines] = useState([]);
  const [filters, setFilters] = useState({ status: "all", category: "all", site_id: "all", search: "" });
  const [view, setView] = useState("grid");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/machines").then((r) => setMachines(r.data));
  }, []);

  const counts = useMemo(() => {
    const c = { total: machines.length, active: 0, idle: 0, maintenance: 0, alert: 0 };
    for (const m of machines) {
      if (m.status === "active") c.active += 1;
      else if (m.status === "idle") c.idle += 1;
      else if (m.status === "maintenance") c.maintenance += 1;
      else if (m.status === "alert") c.alert += 1;
    }
    return c;
  }, [machines]);

  const filtered = useMemo(() => {
    return machines.filter((m) => {
      if (filters.status !== "all" && m.status !== filters.status) return false;
      if (filters.category !== "all" && m.machine_category !== filters.category) return false;
      if (filters.site_id !== "all" && m.site_id !== filters.site_id) return false;
      if (filters.search) {
        const s = filters.search.toLowerCase();
        if (!m.machine_id.toLowerCase().includes(s) && !m.serial_number.includes(s)) return false;
      }
      return true;
    });
  }, [machines, filters]);

  const setStatus = (status) => setFilters((f) => ({ ...f, status: f.status === status ? "all" : status }));

  return (
    <div data-testid="fleet-monitor">
      <PageHeader
        title="Fleet Monitor"
        breadcrumb="Fleet"
        right={
          <>
            <span className="text-[12px]" style={{ color: "var(--slate-500)" }}>
              <span className="font-mono">{formatCount(filtered.length)}</span> machines
            </span>
            <div className="glass-card-sm flex items-center p-0.5">
              <button
                className={`px-2.5 py-1.5 rounded-md flex items-center gap-1.5 text-[12px] ${view === "grid" ? "bg-white/70" : ""}`}
                onClick={() => setView("grid")}
                data-testid="view-grid"
              >
                <Grid3x3 size={13} /> Grid
              </button>
              <button
                className={`px-2.5 py-1.5 rounded-md flex items-center gap-1.5 text-[12px] ${view === "list" ? "bg-white/70" : ""}`}
                onClick={() => setView("list")}
                data-testid="view-list"
              >
                <ListIcon size={13} /> List
              </button>
            </div>
          </>
        }
      />

      {/* KPI strip — click any tile to filter the list by status */}
      <div className="grid grid-cols-4 gap-5 mb-5">
        <ClickableKPITile
          label="Active"
          value={formatCount(counts.active)}
          sub={filters.status === "active" ? "Filter applied · tap to clear" : `${formatPct(counts.total ? counts.active * 100 / counts.total : 0)} of fleet · tap to filter`}
          color="green"
          onClick={() => setStatus("active")}
          testId="kpi-fleet-active"
        />
        <ClickableKPITile
          label="Idle"
          value={formatCount(counts.idle)}
          sub={filters.status === "idle" ? "Filter applied · tap to clear" : "Tap to filter idle"}
          color="amber"
          onClick={() => setStatus("idle")}
          testId="kpi-fleet-idle"
        />
        <ClickableKPITile
          label="Maintenance"
          value={formatCount(counts.maintenance)}
          sub={filters.status === "maintenance" ? "Filter applied · tap to clear" : "Tap to filter maintenance"}
          color="slate"
          onClick={() => setStatus("maintenance")}
          testId="kpi-fleet-maintenance"
        />
        <ClickableKPITile
          label="Alert"
          value={formatCount(counts.alert)}
          sub={filters.status === "alert" ? "Filter applied · tap to clear" : "Tap to filter alerts"}
          color="red"
          onClick={() => setStatus("alert")}
          testId="kpi-fleet-alert"
        />
      </div>

      {/* Filters */}
      <GlassCard className="p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--slate-300)" }} />
          <input
            type="text"
            placeholder="Search by Machine ID or Serial Number"
            className="fleet-input w-full pl-9"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            data-testid="filter-search"
          />
        </div>
        <Select label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })}
                options={STATUSES} testId="filter-status" />
        <Select label="Category" value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })}
                options={CATEGORIES} testId="filter-category" />
        <Select label="Site" value={filters.site_id} onChange={(v) => setFilters({ ...filters, site_id: v })}
                options={SITES.map((s) => s.id)} optionLabels={SITES.map((s) => s.name)} testId="filter-site" />
      </GlassCard>

      {/* Content */}
      {view === "grid" ? (
        <div className="grid grid-cols-4 gap-4">
          {filtered.map((m) => <MachineCard key={m.machine_id} m={m} onClick={setSelected} />)}
        </div>
      ) : (
        <GlassCard className="p-2 overflow-x-auto" data-testid="machine-table">
          <table className="w-full text-[12px]">
            <thead>
              <tr style={{ color: "var(--slate-500)" }}>
                {["Serial", "Machine ID", "Model", "Site", "Status", "Fuel", "Temp", "HMR", "Util %", "Last Update"].map((h) => (
                  <th key={h} className="text-left px-3 py-3 text-[10px] uppercase tracking-wider font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr
                  key={m.machine_id}
                  className="hover:bg-white/40 cursor-pointer border-t"
                  style={{ borderColor: "var(--glass-border-btm)" }}
                  onClick={() => setSelected(m)}
                  data-testid={`row-${m.machine_id}`}
                >
                  <td className="px-3 py-3 font-mono">{m.serial_number}</td>
                  <td className="px-3 py-3 font-medium">{m.machine_id}</td>
                  <td className="px-3 py-3">{m.model}</td>
                  <td className="px-3 py-3">{m.city}</td>
                  <td className="px-3 py-3"><StatusBadge status={m.status} /></td>
                  <td className="px-3 py-3" style={{ width: 120 }}><FuelBar pct={m.fuel_pct} /></td>
                  <td className="px-3 py-3 font-mono">{formatTemp(m.temp_c)}</td>
                  <td className="px-3 py-3 font-mono">{formatHMR(m.total_hmr)}</td>
                  <td className="px-3 py-3 font-mono">{formatPct(m.utilization_pct)}</td>
                  <td className="px-3 py-3 text-[11px]" style={{ color: "var(--slate-500)" }}>{new Date(m.last_updated).toLocaleTimeString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassCard>
      )}

      {selected && <MachineDetailModal machine={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Select({ label, value, onChange, options, optionLabels, testId }) {
  return (
    <select
      className="fleet-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      style={{ minWidth: 140 }}
    >
      {options.map((o, i) => (
        <option key={o} value={o}>
          {label && i === 0 ? `${label}: ` : ""}{optionLabels ? optionLabels[i] : (o === "all" ? "All" : o)}
        </option>
      ))}
    </select>
  );
}

function MachineDetailModal({ machine, onClose }) {
  const [m, setM] = useState(machine);
  const [tab, setTab] = useState("specs");

  useEffect(() => {
    api.get(`/machines/${machine.machine_id}`).then((r) => setM(r.data)).catch(() => {});
  }, [machine.machine_id]);

  const telemetrySeries = useMemo(() => generateTelemetrySeries(m), [m.machine_id]);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: "rgba(44, 40, 37, 0.35)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="machine-modal"
    >
      <div
        className="glass-modal w-[600px] h-full overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
        style={{ borderRadius: "20px 0 0 20px" }}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest mb-1" style={{ color: "var(--slate-500)" }}>
              Machine Detail
            </div>
            <h2 className="font-display text-[26px]" style={{ color: "var(--slate-900)" }}>{m.machine_id}</h2>
            <div className="text-[13px]" style={{ color: "var(--slate-700)" }}>
              {m.model} <span style={{ color: "var(--slate-300)" }}>·</span> <span className="font-mono">{m.serial_number}</span>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost p-2" data-testid="modal-close">
            <X size={14} />
          </button>
        </div>

        {/* Tabs */}
        <div className="glass-card-sm flex items-center gap-1 p-1 mb-4" data-testid="modal-tabs">
          <TabBtn icon={InfoIcon} label="Specs" active={tab === "specs"} onClick={() => setTab("specs")} testId="tab-specs" />
          <TabBtn icon={Activity} label="Telemetry" active={tab === "telemetry"} onClick={() => setTab("telemetry")} testId="tab-telemetry" />
          <TabBtn icon={Wrench} label="Service" active={tab === "service"} onClick={() => setTab("service")} testId="tab-service" />
          <TabBtn icon={MapPin} label="Location" active={tab === "location"} onClick={() => setTab("location")} testId="tab-location" />
        </div>

        {tab === "specs" && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Info label="Status"><StatusBadge status={m.status} /></Info>
              <Info label="Engine #" value={m.engine_number} mono />
              <Info label="Site" value={m.site_name} />
              <Info label="Dealer" value={m.dealer_name} />
              <Info label="Customer" value={m.customer_name} />
              <Info label="Installed" value={m.installation_date} />
            </div>
            <div className="glass-card-sm p-4">
              <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--slate-500)" }}>Telemetry Snapshot</div>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="HMR Total" value={m.total_hmr} />
                <Stat label="Period HMR" value={m.period_hmr} />
                <Stat label="Today HMR" value={`${m.today_hmr}h`} />
                <Stat label="Engine On" value={`${m.engine_on_pct}%`} />
                <Stat label="Working" value={`${m.working_time_pct}%`} />
                <Stat label="Idle" value={`${m.idle_time_pct}%`} />
                <Stat label="Power Low" value={`${m.power_low_pct}%`} />
                <Stat label="Power Med" value={`${m.power_med_pct}%`} />
                <Stat label="Power High" value={`${m.power_high_pct}%`} warn={m.power_high_pct > 50} />
              </div>
            </div>
          </>
        )}

        {tab === "telemetry" && (
          <TelemetryTab machine={m} series={telemetrySeries} />
        )}

        {tab === "service" && (
          <div className="glass-card-sm p-4" data-testid="service-tab">
            <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--slate-500)" }}>
              Service History — {m.service_history?.length || 0} calls
            </div>
            {m.service_history && m.service_history.length > 0 ? (
              <div className="space-y-2">
                {m.service_history.slice(0, 10).map((c) => (
                  <div key={c.id} className="flex items-start justify-between text-[12px] py-1.5 border-b" style={{ borderColor: "var(--glass-border-btm)" }}>
                    <div>
                      <div className="font-mono" style={{ color: "var(--slate-900)" }}>{c.call_id}</div>
                      <div style={{ color: "var(--slate-500)" }}>{c.call_subtype}</div>
                      <div className="text-[10px] mt-0.5" style={{ color: "var(--slate-300)" }}>{c.service_engineer}</div>
                    </div>
                    <StatusBadge status={c.call_status.toLowerCase()} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[13px] py-8 text-center" style={{ color: "var(--slate-500)" }}>
                No service history yet
              </div>
            )}
          </div>
        )}

        {tab === "location" && (
          <div className="glass-card-sm p-4" data-testid="location-tab">
            <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--slate-500)" }}>Location</div>
            <div className="text-[15px] mb-1" style={{ color: "var(--slate-900)" }}>{m.city}, {m.state}</div>
            <div className="font-mono text-[12px] mb-3" style={{ color: "var(--slate-500)" }}>
              {m.lat?.toFixed(6)}, {m.lon?.toFixed(6)}
            </div>
            <div className="flex gap-2 flex-wrap">
              <StatusBadge status={m.device_status?.toLowerCase()} />
              <span className="status-pill copper">IMSI: {m.imsi_number?.slice(0, 8)}…</span>
              <span className="status-pill slate">{m.site_name}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ icon: Icon, label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium transition-all"
      style={{
        background: active ? "rgba(255, 252, 245, 0.85)" : "transparent",
        color: active ? "var(--accent)" : "var(--slate-500)",
        boxShadow: active ? "inset 0 1px 0 rgba(255,255,255,0.6), 0 2px 6px rgba(139,109,56,0.08)" : "none",
      }}
      data-testid={testId}
    >
      <Icon size={12} /> {label}
    </button>
  );
}

function generateTelemetrySeries(machine, days = 30) {
  if (!machine) return [];
  const out = [];
  const today = new Date();
  for (let i = 0; i < days; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - (days - 1 - i));
    const v = () => 1 + (Math.random() - 0.5) * 0.16;
    out.push({
      date: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
      engineOnPct: +((machine.engine_on_pct ?? 30) * v()).toFixed(1),
      idlePct: +((machine.idle_time_pct ?? 10) * v()).toFixed(1),
      powerHighPct: +((machine.power_high_pct ?? 35) * v()).toFixed(1),
      hmr: +(((machine.machine_hours ?? 1000) / days) * v()).toFixed(1),
    });
  }
  return out;
}

function TelemetryTab({ machine, series }) {
  const avgIdle = series.reduce((s, d) => s + d.idlePct, 0) / (series.length || 1);
  const avgHighPower = series.reduce((s, d) => s + d.powerHighPct, 0) / (series.length || 1);
  const avgDailyHmr = series.reduce((s, d) => s + d.hmr, 0) / (series.length || 1);

  const peakBand = avgHighPower > 50 ? "High (>50%)" : avgHighPower > 30 ? "Medium" : "Low";

  let insight;
  if (avgIdle > 15) {
    const waste = Math.round(avgIdle * (machine.machine_hours || 1000) * 0.04 * 90);
    insight = `Idle time averaging ${avgIdle.toFixed(1)}% — above fleet benchmark of 10.8%. Estimated monthly fuel waste: ₹${waste.toLocaleString("en-IN")}.`;
  } else if (avgHighPower > 50) {
    const hrs = Math.round(1000 / avgHighPower * 10);
    insight = `High power band at ${avgHighPower.toFixed(1)}% of engine-on time — elevated wear risk. Schedule hydraulic inspection within ${hrs} operating hours.`;
  } else {
    insight = `Machine operating within normal parameters. Utilization efficiency: ${(100 - avgIdle).toFixed(1)}%.`;
  }

  return (
    <div data-testid="telemetry-tab">
      {/* Mini KPI strip */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <MiniStat label="Avg Daily HMR" value={`${avgDailyHmr.toFixed(1)}h`} color="copper" />
        <MiniStat label="Avg Idle %" value={`${avgIdle.toFixed(1)}%`} color={avgIdle > 15 ? "red" : "green"} />
        <MiniStat label="Peak Power Band" value={peakBand} color={avgHighPower > 50 ? "red" : "amber"} />
      </div>

      {/* Chart */}
      <div className="glass-card-sm p-4 mb-4">
        <div className="flex items-baseline justify-between mb-1">
          <h4 className="font-display text-[14px]" style={{ color: "var(--slate-900)" }}>
            30-Day Operational Telemetry — {machine.machine_id}
          </h4>
        </div>
        <p className="text-[10px] mb-3 italic" style={{ color: "var(--slate-300)" }}>
          Simulated — Connect IoT stream for live data
        </p>
        <div className="chart-box" style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(184,175,164,0.25)" />
              <XAxis dataKey="date" interval={4} tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <RTooltip formatter={(v) => `${v}%`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="engineOnPct" name="Engine On %" stroke="#C4622D" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="idlePct" name="Idle %" stroke="#B07D1A" strokeWidth={2} strokeDasharray="5 3" dot={false} />
              <Line type="monotone" dataKey="powerHighPct" name="High Power Band %" stroke="#B03A2A" strokeWidth={2} strokeDasharray="2 3" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insight */}
      <div className="glass-card-sm p-4" style={{ borderLeft: "3px solid var(--accent)" }}>
        <div className="text-[9px] uppercase tracking-widest mb-1.5 font-semibold" style={{ color: "var(--slate-500)", letterSpacing: "0.12em" }}>
          Telemetry Insight
        </div>
        <p className="text-[12px] leading-relaxed" style={{ color: "var(--slate-700)" }}>
          {insight}
        </p>
      </div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  const colors = {
    copper: "var(--accent)",
    green: "var(--status-green)",
    amber: "var(--status-amber)",
    red: "var(--status-red)",
  };
  return (
    <div className="glass-card-sm p-3 relative overflow-hidden">
      <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full" style={{ background: colors[color] || "var(--slate-500)" }} />
      <div className="pl-2">
        <div className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: "var(--slate-500)" }}>{label}</div>
        <div className="font-display text-[18px]" style={{ color: "var(--slate-900)" }}>{value}</div>
      </div>
    </div>
  );
}

function Info({ label, value, children, mono = false }) {
  return (
    <div className="glass-card-sm p-3">
      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--slate-500)" }}>{label}</div>
      <div className={`${mono ? "font-mono" : "font-body"} text-[13px]`} style={{ color: "var(--slate-900)" }}>
        {children || value || "—"}
      </div>
    </div>
  );
}
function Stat({ label, value, warn }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--slate-500)" }}>{label}</div>
      <div className="font-mono text-[14px]" style={{ color: warn ? "var(--status-red)" : "var(--slate-900)" }}>
        {value}
      </div>
    </div>
  );
}
